import { Box, Grid, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import moment from 'moment';
import type { Moment } from 'moment';
import { MuiFileInput } from 'mui-file-input';
import { ChangeEvent, useContext, useEffect, useState } from 'react';

import type {
  ExternalCompany,
  ReportingPeriod,
  ReportingPeriodGroup,
  Scope,
  Site
} from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';

import ActivityOwnerSelect from '../../shared/input/select/ActivityOwnerSelect';
import type { ActivityOwner } from '../../shared/input/select/ActivityOwnerSelect';
import AlertBanner from '../../shared/banner/AlertBanner';
import PrimaryButton from '../../shared/button/PrimaryButton';
import PrimarySwitch from '../../shared/input/PrimarySwitch';
import ReportingPeriodGroupSelect from '../../shared/input/select/ReportingPeriodGroupSelect';
import ReportingPeriodSelect from '../../shared/input/select/ReportingPeriodSelect';
import SkeletonGrid from '../../shared/loading/SkeletonGrid';
import type { SkeletonItem } from '../../shared/loading/SkeletonGrid';
import { CaptionText } from '../../shared/typography/PrimaryTypography';
import type { ImportContext, ImportContextProps } from '../ImportDataButton';
import { CompanyContext } from '../../../context/CompanyContext';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { UserContext } from '../../../context/UserContext';
import { CLOUD_FUNCTION_URL } from '../../../env';
import {
  getMetricRecordEntitiesMap,
  MetricRecordEntitiesMap
} from '../../../graphql/services/metric_record.service';
import { getAuthHeaderForUser, handleAuthError } from '../../../lib/auth/auth.util';
import type { MetricExtended } from '../../../lib/metric_capture/metric';
import { getCountryIdForSite } from '../../../graphql/utilities/metric_record.util';
import { reportingPeriodFilterByGroup } from '../../../lib/metric_capture/reporting_period';
import {
  findHighestEndDateInGroup,
  findLowestStartDateInGroup
} from '../../../lib/metric_capture/reporting_period_group';
import type { SiteExtended } from '../../../lib/metric_capture/site';
import { generateUuidV4 } from '../../../util/uuid';
import { values } from '../../../util/general_utils';
import { log } from '../../../util/log';
import {
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../../util/global_variables';
import { getUserActivityOwners } from '../../../util/user_access';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/Download.svg';

interface MappedFileImportMetricData {
  [metric_name: string]: {
    id: string;
    name: string;
    group_id: string;
    standard_id: string;
    require_emission_factor: boolean;
    scope?: Scope | null;
    is_calculated?: boolean;
    primary_metric_id?: string;
  };
}

interface MappedFileImportSiteData {
  [site_name: string]: {
    id: string;
    country_id: string | null;
    region_id: string;
    region_name: string;
    subdivision_id: string;
    subdivision_name: string;
    division_id: string;
    division_name: string;
  };
}

export interface MappedFileImportReportingPeriod {
  id: string;
  name: string;
  start: Moment;
  end: Moment;
  locked: boolean;
}

interface MappedFileImportEntityData {
  metrics: MappedFileImportMetricData;
  sites: MappedFileImportSiteData;
  reporting_periods: MappedFileImportReportingPeriod[];
}

interface MappedTemplateMetricData {
  metric: string;
  unit: string;
}

interface MappedTemplateSiteData {
  site: string;
  subdivision: string;
  division: string;
}

interface TemplateEntityData {
  metrics: MappedTemplateMetricData[];
  sites: MappedTemplateSiteData[];
  reporting_period?: string;
  reporting_period_group?: string;
}

// Data sent in request to unique Cloud Function.
interface IMetricRecordFileImportContext extends ImportContext {
  source: string;
  group_id: string;
  company_id: string;
  reporting_period?: string;
  reporting_period_group?: string;
  external_company: string | null;
  metric_records: File | null;
  mapping: (() => Promise<File>) | null;
}

const SOURCE = 'MetricRecordFileImportContext.tsx';

/**
 * Reusable importing context for metric record file data containing relevant inputs for the microservice.
 * @param handleContextChange Update context in parent from input changes.
 * @param handleContextValid Update context validity in parent based on input changes.
 */
function MetricRecordFileImportContext({
  handleContextChange,
  handleContextValid
}: ImportContextProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) return null;

  const initialContext: IMetricRecordFileImportContext = {
    source: '',
    group_id: group?.id ?? '',
    company_id: company?.id ?? '',
    reporting_period_group: '',
    reporting_period: '',
    external_company: null,
    metric_records: null,
    mapping: null
  };

  const initialEntitiesMap: MetricRecordEntitiesMap = {
    countries: {},
    regions: {},
    divisions: {},
    subdivisions: {},
    sites: {},
    reporting_period_groups: {},
    reporting_periods: {},
    metrics: {},
    emission_factors: {},
    external_companies: {}
  };

  const [entitiesMap, setEntitiesMap] = useState<MetricRecordEntitiesMap>(initialEntitiesMap);
  const [reportingPeriodGroup, setReportingPeriodGroup] = useState<ReportingPeriodGroup | null>(
    null
  );
  const [reportingPeriod, setReportingPeriod] = useState<ReportingPeriod | null>(null);
  const [activityOwner, setActivityOwner] = useState<ActivityOwner | null>(null);
  const [metricRecordFileImportContext, setMetricRecordFileImportContext] =
    useState<IMetricRecordFileImportContext>(initialContext);
  const [averageOverYear, setAverageOverYear] = useState<boolean>(false);
  const [loadingTemplate, setLoadingTemplate] = useState<boolean>(false);
  const [loadingEntities, setLoadingEntities] = useState<boolean>(true);

  const reportingPeriodGroups = values<ReportingPeriodGroup>(entitiesMap.reporting_period_groups);
  const reportingPeriods = values<ReportingPeriod>(entitiesMap.reporting_periods);
  const metrics = values<MetricExtended>(entitiesMap.metrics);
  const sites = values<SiteExtended>(entitiesMap.sites);
  const externalCompanies = values<ExternalCompany>(entitiesMap.external_companies);

  const activityOwnerOptions: ActivityOwner[] =
    user && company && group
      ? getUserActivityOwners(user, group.id, company, externalCompanies)
      : [];

  const quantitativeMetrics: MetricExtended[] = metrics.filter(
    (metric: MetricExtended) =>
      'is_quantitative' in metric.standard && metric.standard.is_quantitative
  );

  const averagedReportingPeriod: ReportingPeriod = {
    id: 'all',
    start:
      reportingPeriodGroup && reportingPeriodGroup.id !== 'all'
        ? findLowestStartDateInGroup(reportingPeriodGroups, reportingPeriods, reportingPeriodGroup)
        : moment(),
    end:
      reportingPeriodGroup && reportingPeriodGroup.id !== 'all'
        ? findHighestEndDateInGroup(reportingPeriodGroups, reportingPeriods, reportingPeriodGroup)
        : moment(),
    deleted: null,
    locked: false,
    name: 'Average Over Group'
  };

  const contextValid = Boolean(
    metricRecordFileImportContext.group_id &&
    metricRecordFileImportContext.company_id &&
    reportingPeriodGroup &&
    reportingPeriod &&
    metricRecordFileImportContext.metric_records &&
    activityOwner
  );

  useEffect(() => {
    handleContextValid(contextValid);
  }, [contextValid, handleContextValid]);

  const handleReportingPeriodGroupChange = (
    updatedReportingPeriodGroup: ReportingPeriodGroup[] | ReportingPeriodGroup | null
  ): void => {
    if (!Array.isArray(updatedReportingPeriodGroup)) {
      const updatedContext: IMetricRecordFileImportContext = {
        ...metricRecordFileImportContext,
        reporting_period_group: updatedReportingPeriodGroup
          ? updatedReportingPeriodGroup.id === 'all'
            ? ''
            : updatedReportingPeriodGroup.id
          : '',
        reporting_period: ''
      };
      setReportingPeriodGroup(updatedReportingPeriodGroup);
      setReportingPeriod(null);
      setMetricRecordFileImportContext(updatedContext);
      handleContextChange(updatedContext);
    }
  };

  const handleReportingPeriodChange = (
    updatedReportingPeriod: ReportingPeriod[] | ReportingPeriod | null
  ): void => {
    if (!Array.isArray(updatedReportingPeriod)) {
      const updatedContext: IMetricRecordFileImportContext = {
        ...metricRecordFileImportContext,
        reporting_period_group:
          updatedReportingPeriod && updatedReportingPeriod.id !== 'all'
            ? ''
            : reportingPeriodGroup?.id,
        reporting_period:
          updatedReportingPeriod && updatedReportingPeriod.id !== 'all'
            ? updatedReportingPeriod.id
            : ''
      };
      setReportingPeriod(updatedReportingPeriod);
      setMetricRecordFileImportContext(updatedContext);
      handleContextChange(updatedContext);
    }
  };

  // Set the flag for averaging csv values for the reporting period group and update the reporting period input accordingly.
  const handleAverageOverYearChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAverageOverYear(event.target.checked);
    setReportingPeriod(event.target.checked ? averagedReportingPeriod : null);
  };

  const handleActivityOwnerChange = (updatedActivityOwner: ActivityOwner | null): void => {
    // Look for external company matching activity owner input, null if internal selection id (no external company).
    const externalCompany: ExternalCompany | undefined = updatedActivityOwner
      ? entitiesMap.external_companies[updatedActivityOwner.id]
      : undefined;
    const updatedContext: IMetricRecordFileImportContext = {
      ...metricRecordFileImportContext,
      external_company: externalCompany?.id ?? null
    };
    setActivityOwner(updatedActivityOwner);
    setMetricRecordFileImportContext(updatedContext);
    handleContextChange(updatedContext);
  };

  const handleFileChange = (newFile: File | null): void => {
    const updatedContext: IMetricRecordFileImportContext = {
      ...metricRecordFileImportContext,
      metric_records: newFile,
      source: newFile?.name ?? ''
    };
    setMetricRecordFileImportContext(updatedContext);
    handleContextChange(updatedContext);
  };

  const processEntitiesMapForTemplate = (): TemplateEntityData => {
    const mappedSites: MappedTemplateSiteData[] = sites.map((site: Site) => {
      if (!('name' in site.subdivision) || !('name' in site.subdivision.division)) {
        throw new Error(
          'Invalid site data found for download template, missing division, subdivision names'
        );
      }
      return {
        site: site.name,
        division: site.subdivision.division.name,
        subdivision: site.subdivision.name ?? ''
      };
    });
    const mappedMetrics: MappedTemplateMetricData[] = quantitativeMetrics
      .filter((metric: MetricExtended) => !metric.is_calculated)
      .map((metric: MetricExtended) => {
        return {
          metric: `${metric.metric_group.name}: ${metric.name}`,
          unit: metric.unit ? metric.unit : ''
        };
      });
    return {
      sites: mappedSites,
      metrics: mappedMetrics
    };
  };

  const downloadTemplateHandler = async (): Promise<void> => {
    try {
      setLoadingTemplate(true);
      if (!group || !company || !reportingPeriodGroup) {
        throw new Error(
          'Missing group, company and reporting period group data required by template download.'
        );
      }
      const mappedEntities: TemplateEntityData = processEntitiesMapForTemplate();
      const dataObject: TemplateEntityData =
        reportingPeriod && reportingPeriod.id !== 'all'
          ? {
              sites: mappedEntities.sites,
              metrics: mappedEntities.metrics,
              reporting_period: reportingPeriod.name
            }
          : {
              sites: mappedEntities.sites,
              metrics: mappedEntities.metrics,
              reporting_period_group: reportingPeriodGroup!.name
            };
      const response: AxiosResponse = await axios({
        method: 'post',
        url: `${CLOUD_FUNCTION_URL}/generateImportTemplate`,
        data: dataObject,
        headers: {
          ...(await getAuthHeaderForUser())
        }
      }).catch((error) => {
        handleAuthError(error);
        throw error.response;
      });
      if (response) {
        const url: string = window.URL.createObjectURL(new Blob([response.data]));
        const link: HTMLAnchorElement = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${company.id}_${
            reportingPeriod && reportingPeriod.id !== 'all'
              ? reportingPeriod.name.replace(/ /g, '_').toLowerCase()
              : reportingPeriodGroup!.name.replace(/ /g, '_').toLowerCase()
          }_import.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'downloadTemplateHandler'),
          {
            metricRecordFileImportContext,
            entitiesMap
          },
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to download import template', trackingId),
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingTemplate(false);
    }
  };

  const processSitesForMappingFile = (): MappedFileImportSiteData => {
    return sites.reduce((accumulator: MappedFileImportSiteData, site: SiteExtended) => {
      accumulator[site.name.toLowerCase()] = {
        id: site.id,
        country_id: getCountryIdForSite(site),
        region_id: site.region.id,
        region_name: site.region.name.toLowerCase(),
        subdivision_id: site.subdivision.id,
        subdivision_name: site.subdivision.name.toLowerCase(),
        division_id: site.division.id,
        division_name: site.division.name.toLowerCase()
      };
      return accumulator;
    }, {});
  };

  const processMetricsForMappingFile = async (): Promise<MappedFileImportMetricData> => {
    const mappedMetricsArray: MappedFileImportMetricData[] = quantitativeMetrics.map(
      (metric: MetricExtended) => {
        const requireEmissionFactor: boolean =
          'require_emission_factor' in metric.standard && metric.standard.require_emission_factor;

        const resolvedScope: Scope | null = activityOwner?.is_external
          ? (activityOwner.scope as Scope)
          : metric.scope
            ? (metric.scope as Scope)
            : null;

        return {
          [metric.name.toLowerCase()]: {
            id: metric.id,
            name: metric.name,
            group_id: metric.metric_group.id,
            standard_id: metric.standard.id,
            require_emission_factor: requireEmissionFactor,
            scope: resolvedScope,
            is_calculated: metric.is_calculated,
            primary_metric_id: metric.primary_metric_calculated?.id
          }
        };
      }
    );

    return mappedMetricsArray.reduce(
      (metricMap: MappedFileImportMetricData, nextMetric: MappedFileImportMetricData) => ({
        ...metricMap,
        ...nextMetric
      }),
      {}
    );
  };

  const processReportingPeriodsForMappingFile = (): MappedFileImportReportingPeriod[] => {
    const reportingPeriodToImportData = (
      period: ReportingPeriod
    ): MappedFileImportReportingPeriod => ({
      id: period.id,
      name: period.name,
      start: moment(period.start),
      end: moment(period.end),
      locked: period.locked
    });
    if (averageOverYear) {
      return reportingPeriodFilterByGroup(reportingPeriodGroup?.id ?? 'all', reportingPeriods).map(
        reportingPeriodToImportData
      );
    }
    return reportingPeriod ? [reportingPeriodToImportData(reportingPeriod)] : [];
  };

  // The generator function assigned to mapping file field needs updated state values, hacky and Cloud Function should probably be refactored to do mapping logic.
  const generateMappingFile = async (): Promise<File> => {
    const siteData: MappedFileImportSiteData = processSitesForMappingFile();
    const metricData: MappedFileImportMetricData = await processMetricsForMappingFile();
    const reportingPeriodData: MappedFileImportReportingPeriod[] =
      processReportingPeriodsForMappingFile();

    const mappedEntities: MappedFileImportEntityData = {
      sites: siteData,
      metrics: metricData,
      reporting_periods: reportingPeriodData
    };
    return new File([JSON.stringify(mappedEntities)], 'mapping.json', {
      type: 'application/json'
    });
  };

  const fetchMetricRecordEntitiesMap = async () => {
    setLoadingEntities(true);
    try {
      if (group && company && user) {
        const entitiesMapResult: MetricRecordEntitiesMap = await getMetricRecordEntitiesMap(
          group.id,
          company.id,
          user
        );
        if (entitiesMapResult) {
          setEntitiesMap({
            countries: entitiesMapResult.countries,
            regions: entitiesMapResult.regions,
            divisions: entitiesMapResult.divisions,
            subdivisions: entitiesMapResult.subdivisions,
            sites: entitiesMapResult.sites,
            reporting_period_groups: entitiesMapResult.reporting_period_groups,
            reporting_periods: entitiesMapResult.reporting_periods,
            metrics: entitiesMapResult.metrics,
            emission_factors: entitiesMapResult.emission_factors,
            external_companies: entitiesMapResult.external_companies
          });
        }
      }
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchMetricRecordEntitiesMap'),
          {
            group,
            company,
            user
          },
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch context entities', trackingId),
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  useEffect(() => {
    (async () => {
      setEntitiesMap(initialEntitiesMap);
      setMetricRecordFileImportContext(initialContext);
      setReportingPeriodGroup(null);
      setReportingPeriod(null);
      setActivityOwner(null);
      setAverageOverYear(false);
      if (group && company && user) {
        await fetchMetricRecordEntitiesMap();
      }
    })();
  }, [user, company, group]);

  // Set activity owner automatically if internal user or external with 1 external company.
  useEffect(() => {
    try {
      if (user) {
        if (activityOwnerOptions.length === 1) {
          setActivityOwner(activityOwnerOptions[0]);
        }
      }
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
          null,
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to set Activity Owner', trackingId),
        state: true,
        type: 'error'
      });
    }
  }, [user]);

  useEffect(() => {
    handleContextChange({ ...metricRecordFileImportContext, mapping: generateMappingFile });
  }, [metricRecordFileImportContext]);

  const skeletonGridItems: SkeletonItem[] = Array(4).fill({
    width: 250,
    height: 50,
    variant: 'rounded'
  });

  return (
    <>
      {loadingEntities ? (
        <SkeletonGrid skeleton_items={skeletonGridItems} />
      ) : (
        <>
          <Grid size={'auto'} sx={{ minWidth: 250 }}>
            <ReportingPeriodGroupSelect
              selected_reporting_period_group={reportingPeriodGroup}
              reporting_period_group_options={reportingPeriodGroups}
              handleChangeReportingPeriodGroups={handleReportingPeriodGroupChange}
            />
          </Grid>
          <Grid size={'auto'} sx={{ display: 'flex', minWidth: 250, gap: 1, pt: 2 }}>
            <CaptionText weight="Medium">Monthly Data</CaptionText>
            <PrimarySwitch
              disableRipple
              checked={averageOverYear}
              onChange={handleAverageOverYearChange}
            />
            <CaptionText weight="Medium">Yearly Data</CaptionText>
            <Tooltip
              title={
                <Typography fontSize="0.9rem">
                  Capturing yearly data will average the values down for each reporting month within
                  the financial year ( / 12).
                </Typography>
              }
              arrow
            >
              <InfoOutlinedIcon color="primary" fontSize="small" />
            </Tooltip>
          </Grid>
          {!averageOverYear && (
            <Grid size={'auto'} sx={{ minWidth: 250 }}>
              <ReportingPeriodSelect
                selected_reporting_periods={reportingPeriod}
                reporting_period_options={reportingPeriodFilterByGroup(
                  reportingPeriodGroup?.id ?? 'all',
                  reportingPeriods
                ).filter((period: ReportingPeriod) => !period.locked)}
                handleChangeReportingPeriods={handleReportingPeriodChange}
                disabled={!reportingPeriodGroup || reportingPeriods.length === 0}
              />
            </Grid>
          )}
          {activityOwnerOptions.length > 1 && (
            <Grid size={'auto'} sx={{ minWidth: 250 }}>
              <ActivityOwnerSelect
                options={activityOwnerOptions}
                selected_options={activityOwner}
                clearable={false}
                handleChangeActivityOwner={(owner: ActivityOwner | ActivityOwner[] | null) => {
                  if (!Array.isArray(owner)) {
                    handleActivityOwnerChange(owner);
                  }
                }}
              />
            </Grid>
          )}
          <Grid size={'auto'} sx={{ minWidth: 250, display: 'flex', flexDirection: 'column' }}>
            <MuiFileInput
              value={metricRecordFileImportContext.metric_records}
              onChange={handleFileChange}
              title="Choose Data File"
              label="Data File"
              inputProps={{ accept: '.csv' }}
            />
            <Tooltip title="Download Template">
              <span style={{ display: 'block', marginTop: 12, width: 'fit-content' }}>
                <PrimaryButton
                  loading={loadingTemplate}
                  onClick={downloadTemplateHandler}
                  disabled={
                    !metricRecordFileImportContext.company_id ||
                    !reportingPeriodGroup ||
                    (reportingPeriodGroup.id === 'all' && !reportingPeriod)
                  }
                  startIcon={
                    <DownloadIcon height={25} fill="none" stroke="currentColor" strokeWidth={2} />
                  }
                  sx={{ mr: 'auto' }}
                >
                  Download Template
                </PrimaryButton>
              </span>
            </Tooltip>
          </Grid>
          {averageOverYear && reportingPeriod?.id === 'all' && (
            <Grid size={12}>
              <AlertBanner
                severity="warning"
                message={
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', display: 'inline-flex' }}>
                      Capturing on Reporting Period Group level - Values will be divided evenly
                      between Reporting Periods.
                    </Typography>
                  </Box>
                }
                open
                width_perc={100}
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
}

export default MetricRecordFileImportContext;
